import { useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Star, Trash2, Loader2, EyeOff, Eye, Maximize2 } from "lucide-react";
import { VideoUploader } from "@/components/video/VideoUploader";
import { StreamThumb } from "@/components/video/StreamThumb";
import { mediaRawUrl, uploadAdPhoto, removeAdPhoto } from "@/lib/api";
import { adSequence, aid, withInserted, saveSequence } from "@/lib/adMedia";
import { DropZone, splitFiles } from "@/components/apply/DropZone";
import { RotateControls } from "@/components/RotateControls";
import { ConfirmDelete } from "@/components/adtemplate/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PHOTO_EXTS = ["jpg", "jpeg", "png", "heic", "heif", "webp"];
const byKind = (media, kind) => {
  const xs = (media || []).filter((m) => m.kind === kind);
  return [...xs.filter((m) => m.ad_order).sort((a, b) => a.ad_order - b.ad_order), ...xs.filter((m) => !m.ad_order)];
};

function Tile({ m, horseId, n, locked, kind, onCover, onToggle, onRemove, onPreview, busy, onRotated }) {
  const src = mediaRawUrl(horseId, aid(m), m.rotation);
  const t = `ad-${kind}-`;
  return (
    <div data-testid={`${t}${aid(m)}`} className={`rounded-xl border bg-white overflow-hidden ${m.ad_order ? "border-stone-200" : "border-dashed border-stone-300"}`}>
      <button type="button" data-testid={`${t}preview-${aid(m)}`} onClick={onPreview} className={`relative block w-full aspect-[4/3] bg-stone-100 group ${m.ad_order ? "" : "opacity-60"}`}>
        {kind === "image" ? <img src={src} alt={m.name} loading="lazy" className="w-full h-full object-cover" />
          : m.source === "stream" ? <img src={src} alt={m.name} loading="lazy" className="w-full h-full object-cover bg-stone-800" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
          : <video src={src} preload="metadata" muted playsInline className="w-full h-full object-cover" />}
        {n ? <span data-testid={`${t}order-${aid(m)}`} className="absolute top-1.5 left-1.5 rounded-full bg-stone-900/80 text-white text-[11px] font-bold px-2 py-0.5">#{n}{kind === "image" && n === 1 && " Main"}</span>
          : <span className="absolute top-1.5 left-1.5 rounded-full bg-white/90 text-stone-600 text-[11px] font-semibold px-2 py-0.5">Not in ad</span>}
        <Maximize2 className="absolute top-1.5 right-1.5 h-4 w-4 text-white drop-shadow opacity-80 group-hover:opacity-100" />
      </button>
      {!locked && (
        <div className="flex flex-wrap items-center justify-end gap-1 p-1">
          {kind === "image" && <RotateControls horseId={horseId} m={m} onRotated={onRotated} testId={`${t}rotate-${aid(m)}`} />}
          {kind === "image" && <Button size="icon" variant="ghost" data-testid={`${t}cover-${aid(m)}`} disabled={n === 1} onClick={onCover} className="h-11 w-11 sm:h-9 sm:w-9" aria-label="Make main photo"><Star className="h-4 w-4" /></Button>}
          <Button size="icon" variant="ghost" data-testid={`${t}toggle-${aid(m)}`} onClick={onToggle} className="h-11 w-11 sm:h-9 sm:w-9" aria-label={m.ad_order ? "Leave out of ad" : "Use in ad"}>{m.ad_order ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
          {onRemove && <Button size="icon" variant="ghost" data-testid={`${t}remove-${aid(m)}`} disabled={busy} onClick={onRemove} className="h-11 w-11 sm:h-9 sm:w-9 text-red-700" aria-label="Delete attachment" title="Delete attachment"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      )}
    </div>
  );
}

function MediaGrid({ kind, horseId, media, locked, onChange, busy, onRemove }) {
  const [preview, setPreview] = useState(null);
  const list = byKind(media, kind);
  const seq = adSequence(media);
  const save = (next) => saveSequence(horseId, media, next, onChange);
  if (!list.length) return null;
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {list.map((m) => {
          const idx = seq.indexOf(m);
          return (
            <Tile key={aid(m)} m={m} horseId={horseId} kind={kind} n={idx >= 0 ? idx + 1 : null} locked={locked} busy={busy === aid(m)}
              onCover={() => save([m, ...seq.filter((x) => x !== m)])}
              onToggle={() => save(m.ad_order ? seq.filter((x) => x !== m) : withInserted(seq, m))}
              onRotated={(r) => onChange(media.map((x) => (x === m ? { ...x, rotation: r } : x)))}
              onRemove={m.source === "application" && onRemove ? () => onRemove(m) : null} onPreview={() => setPreview(m)} />
          );
        })}
      </div>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid={`ad-${kind}-lightbox`} className="max-w-3xl p-2 sm:p-3">
          <DialogTitle className="sr-only">{preview?.name || "Preview"}</DialogTitle>
          <DialogDescription className="sr-only">Full-size preview</DialogDescription>
          {preview && (kind === "image"
            ? <img src={mediaRawUrl(horseId, aid(preview), preview.rotation)} alt={preview.name} className="w-full max-h-[80vh] object-contain rounded-lg" />
            : preview.source === "stream" ? <StreamThumb videoId={preview.video_id} name={preview.name} inline className="aspect-video" />
            : <video src={mediaRawUrl(horseId, aid(preview))} controls playsInline className="w-full max-h-[80vh] rounded-lg" />)}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function SubHead({ letter, title, testId, children }) {
  return (
    <div data-testid={testId} className="space-y-3">
      <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-stone-800">
        <span className="h-6 w-6 rounded-full bg-amber-800 text-white text-xs flex items-center justify-center">{letter}</span>{title}
      </h3>
      {children}
    </div>
  );
}

export function AdPhotos({ horseId, media, locked, onChange, onReload, adminView = false }) {
  const [busy, setBusy] = useState(null);
  const [del, setDel] = useState(null);
  const remove = async () => {
    const m = del; setDel(null);
    setBusy(aid(m));
    try { onChange(await removeAdPhoto(horseId, aid(m))); toast.success("Photo removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
    finally { setBusy(null); }
  };
  const upload = async (files) => {
    const { ok, error } = splitFiles(files, PHOTO_EXTS, "Use JPG, PNG, HEIC or WEBP photos.");
    if (error) toast.error(error);
    for (const f of ok) {
      setBusy(`up:${f.name}`);
      try { onChange(await uploadAdPhoto(horseId, f)); } catch (e) { toast.error(`${f.name}: ${e.response?.data?.detail || "upload failed"}`); }
    }
    setBusy(null);
    if (ok.length) toast.success("Photos added");
  };
  const photos = byKind(media, "image");
  const videos = byKind(media, "video");

  return (
    <div data-testid="ad-photos" className="space-y-8">
      <SubHead letter="B" title="Horse Photos" testId="ad-photos-section">
        <p className="text-sm text-stone-600">Photos from your application are already here. Tap a photo to preview it. The star makes a photo the <strong>main</strong> ad photo, the eye leaves it out of the ad (it stays saved). Reorder everything in <strong>Advertisement Order</strong> below.</p>
        <MediaGrid kind="image" horseId={horseId} media={media} locked={locked} onChange={onChange} busy={busy} onRemove={setDel} />
        <ConfirmDelete item={del} kind="photo" onCancel={() => setDel(null)} onConfirm={remove} note="To keep the photo but leave it out of the ad, use the eye button instead." />
        {!photos.length && <p data-testid="ad-photos-empty" className="text-sm text-stone-500 bg-stone-50 border border-dashed border-stone-300 rounded-xl p-4">No photos yet. Add your best photos of the horse.</p>}
        {!locked && (
          <DropZone testId="ad-photo-upload" accept={PHOTO_EXTS.map((e) => `.${e}`).join(",")} onFiles={upload} disabled={!!busy}
            icon={ImagePlus} label="Add More Photos" dropLabel="Drop photos to add" />
        )}
        {busy?.startsWith("up:") && <div data-testid="ad-photo-uploading" className="flex items-center gap-2 text-sm text-stone-600"><Loader2 className="h-4 w-4 animate-spin" /> Uploading {busy.slice(3)}…</div>}
      </SubHead>

      <SubHead letter="C" title="Horse Videos" testId="ad-videos-section">
        <p className="text-sm text-stone-600">Videos from your application are already here. The eye leaves a video out of the ad without deleting it.</p>
        <MediaGrid kind="video" horseId={horseId} media={media} locked={locked} onChange={onChange} busy={busy} />
        {!videos.length && <p data-testid="ad-videos-empty" className="text-sm text-stone-500">No videos on file yet.</p>}
        <VideoUploader scope={{ type: "horse", id: horseId }} readOnly={locked} adminView={adminView} onChanged={onReload} title="Upload &amp; Manage Videos" />
      </SubHead>
    </div>
  );
}
